import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';


const TemplatesPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const reduxUser = useSelector((state) => state.auth?.authData);

    // User state management
    const getStoredUser = () => {
        if (reduxUser?.result) return reduxUser.result;
        const localUser = JSON.parse(localStorage.getItem("user_info"));
        if (localUser?.result) return localUser.result;
        return null;
    };

    const [currentUser, setCurrentUser] = useState(getStoredUser);

    // Sync Redux with localStorage
    useEffect(() => {
        const storedUser = getStoredUser();
        setCurrentUser(storedUser);
        if (!reduxUser && storedUser) {
            dispatch({ type: "AUTH", data: storedUser });
        }
    }, [reduxUser, dispatch]);

    // Check package access
    useEffect(() => {
        const checkAccess = () => {
            // First check if user is logged in
            if (!currentUser) {
                toast.error("Please login to access templates");
                navigate("/login");
                return;
            }

            // Then check package access for logged-in users
            const userPackage = currentUser.package || localStorage.getItem("userPackage");
            const allowedPackages = ["30-DAYS-PACKAGE", "60-DAYS-PACKAGE", "90-DAYS-PACKAGE"];

            if (!allowedPackages.includes(userPackage)) {
                toast.info("Templates require a premium package subscription");
                navigate("/pricing");
                return;
            }
        };

        // Only run checkAccess if we've finished checking user state
        if (currentUser === null || currentUser) {
            checkAccess();
        }
    }, [currentUser, navigate]);

    const handleSubscribe = () => {
        navigate("/pricing");
    };

    return (
        <div className="bg-white min-h-screen">
            {/* Hero Section */}
            <section className="bg-white">
                <div className="max-w-6xl mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-2 items-center gap-6">
                    <img
                        src="https://engdemyeducation.com/image/writingbanner.png"
                        alt="Best Writing Templates"
                        className="w-full h-auto"
                    />
                    <div>
                        <h1 className="text-4xl font-bold text-red-700 mb-4">BEST WRITING TEMPLATES</h1>
                        <p className="text-2xl font-semibold text-black">FREE TEMPLATES TO GET A HIGH SCORE</p>
                    </div>
                </div>
            </section>

            {/* Product Boxes */}
            <section className="max-w-4xl mx-auto mt-10 px-4">
                <div className="flex flex-wrap justify-center gap-6">
                    {/* Writing Template */}
                    <div className="bg-white border rounded-md p-4 w-32 text-center shadow-sm hover:shadow-md transition">
                        <img
                            src="https://engdemyeducation.com/image/writingtemplate.png"
                            alt="Writing Template"
                            className="w-16 h-16 object-cover mx-auto mb-2"
                        />
                        <h2 className="text-sm font-semibold text-gray-800">Writing Template</h2>
                        <p className="text-xs text-gray-500 mb-2">Premium Access</p>
                        <button
                            onClick={handleSubscribe}
                            className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-full"
                        >
                            Subscribe
                        </button>
                    </div>

                    {/* Speaking Template */}
                    <div className="bg-white border rounded-md p-4 w-32 text-center shadow-sm hover:shadow-md transition">
                        <img
                            src="https://engdemyeducation.com/image/speakingtemplate.png"
                            alt="Speaking Template"
                            className="w-16 h-16 object-cover mx-auto mb-2"
                        />
                        <h2 className="text-sm font-semibold text-gray-800">Speaking Template</h2>
                        <p className="text-xs text-gray-500 mb-2">Premium Access</p>
                        <button
                            onClick={handleSubscribe}
                            className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-full"
                        >
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>

            {/* Email Templates */}
            <section className="max-w-3xl mx-auto mt-14 px-6 text-sm text-gray-800 leading-relaxed">
                <h2 className="text-xl font-semibold text-red-700 mb-3">INFORMAL EMAIL TEMPLATE LINES</h2>
                <p><strong>Greeting:</strong> Hopefully, you and your husband are doing well...</p>
                <p><strong>Apology:</strong> I just realized I still have the [item]...</p>
                <p><strong>Importance:</strong> Is it something important that you really need?</p>
                <p><strong>What you'll do:</strong> I can send it to you by courier...</p>
                <p><strong>Closing:</strong> Do get back to me when you can. Take care!</p>

                <hr className="my-6" />

                <h2 className="text-xl font-semibold text-blue-700 mb-3">SEMI-FORMAL EMAIL TEMPLATE LINES</h2>
                <p><strong>Greeting:</strong> I hope you are doing well...</p>
                <p><strong>Apology:</strong> I’ve only now realized I still have the book...</p>
                <p><strong>Importance:</strong> Could you please let me know how important it is?</p>
                <p><strong>What you'll do:</strong> I’d be happy to mail it or make arrangements...</p>
                <p><strong>Closing:</strong> I look forward to hearing from you soon.</p>

                <hr className="my-6" />

                <h2 className="text-xl font-semibold text-gray-900 mb-3">FORMAL EMAIL TEMPLATE LINES</h2>
                <p><strong>Greeting:</strong> Dear [Mr./Ms./Dr.], I hope this message finds you well.</p>
                <p><strong>Apology:</strong> I regret any inconvenience caused by retaining the book.</p>
                <p><strong>Importance:</strong> Please let me know if it is important and needs to be returned.</p>
                <p><strong>What you'll do:</strong> I’ll make arrangements to courier it promptly.</p>
                <p><strong>Closing:</strong> Thank you for your understanding. Yours sincerely, [Your Name]</p>
            </section>

            {/* CELPIP Speaking Answers */}
            <section className="max-w-3xl mx-auto mt-14 px-6 text-sm text-gray-800 leading-relaxed">
                <h2 className="text-xl font-semibold text-green-700 mb-4">CELPIP SPEAKING SAMPLE RESPONSES</h2>

                <p><strong>Task 1 – Giving Advice:</strong><br />
                    “If I were in your shoes, I’d definitely consider the new job.<br />
                    The change might feel risky, but it brings growth.<br />
                    You’ve been feeling stuck lately—this could motivate you.<br />
                    Just weigh the pros and cons first.”</p>

                <p className="mt-4"><strong>Task 2 – Personal Experience:</strong><br />
                    “Last year, I helped my neighbor move into their new apartment.<br />
                    They were overwhelmed with packing, so I helped all weekend.<br />
                    It felt great to support them.<br />
                    We became closer friends after that.”</p>

                <p className="mt-4"><strong>Task 3 – Describing a Scene:</strong><br />
                    “This looks like a busy coffee shop in the morning.<br />
                    A barista is preparing drinks while customers wait.<br />
                    Some people are working on laptops.<br />
                    The atmosphere feels lively and energetic.”</p>

                <p className="mt-4"><strong>Task 4 – Making Predictions:</strong><br />
                    “I think the child is about to knock over the vase.<br />
                    It’s close to the edge of the table and looks unstable.<br />
                    An adult might intervene just in time.<br />
                    If not, the vase will break and cause a mess.”</p>

                <p className="mt-4"><strong>Task 5 – Comparing & Persuading:</strong><br />
                    “I’d definitely choose to live in the city.<br />
                    It has better job options, transport, and healthcare.<br />
                    Life is faster, but full of opportunities.<br />
                    That’s why I recommend city life.”</p>

                <p className="mt-4"><strong>Task 6 – Difficult Situation:</strong><br />
                    “Hi, I’m calling because my flight was just cancelled.<br />
                    I was supposed to fly to Toronto this afternoon.<br />
                    Is there any chance I can be rebooked today?<br />
                    It’s really important that I reach on time.”</p>

                <p className="mt-4"><strong>Task 7 – Expressing Opinions:</strong><br />
                    “I believe school uniforms are a great idea.<br />
                    They reduce social pressure and promote equality.<br />
                    Students focus more on learning than clothing.<br />
                    It helps create a better environment.”</p>

                <p className="mt-4"><strong>Task 8 – Unusual Situation:</strong><br />
                    “You won’t believe what happened today!<br />
                    I lost my wallet but someone returned it with everything inside.<br />
                    They found my contact details and called me.<br />
                    I was really touched by their honesty.”</p>
            </section>

            {/* CELPIP Speaking Starters */}
            <section className="max-w-3xl mx-auto mt-14 px-6 text-sm text-gray-800 leading-relaxed">
                <h2 className="text-xl font-semibold text-indigo-700 mb-4">CELPIP SPEAKING STARTER LINES</h2>

                <p><strong>Task 1 – Giving Advice:</strong> “Let me give you my honest opinion…”, “If I were you, I’d…”</p>
                <p><strong>Task 2 – Personal Experience:</strong> “This is a memory I’ll never forget…”, “Let me tell you a story…”</p>
                <p><strong>Task 3 – Describing a Scene:</strong> “There’s a lot happening here…”, “To the left of the picture…”</p>
                <p><strong>Task 4 – Making Predictions:</strong> “Something tells me that…”, “I think the next thing will be…”</p>
                <p><strong>Task 5 – Comparing and Persuading:</strong> “I believe option A is better because…”, “Here’s why I’d choose…”</p>
                <p><strong>Task 6 – Difficult Situation:</strong> “Hi, I need help with an urgent issue…”, “I’d like to explain a problem…”</p>
                <p><strong>Task 7 – Expressing Opinions:</strong> “Personally, I feel that…”, “Here’s my perspective on the matter…”</p>
                <p><strong>Task 8 – Unusual Situation:</strong> “You won’t believe what happened…”, “Something really strange occurred…”</p>
            </section>

            {/* Footer */}
            <footer className="text-center text-sm text-gray-500 mt-16 mb-8 w-full max-w-6xl px-4 sm:px-6 lg:px-8">
                &copy; {new Date().getFullYear()} Engdemy Education. All rights reserved.
            </footer>
        </div>
    );
}

export default TemplatesPage;
